/**
 * useStripeCheckout Hook
 * Handle Stripe Checkout and Customer Portal redirects
 *
 * Provides methods to:
 * - Create checkout session and redirect to Stripe
 * - Open customer portal for billing management
 * - Handle loading and error states
 */

import { useState } from 'react'
import { billingService } from '../services/billingService'
import { message } from 'antd'

export function useStripeCheckout() {
  const [loading, setLoading] = useState(false)

  /**
   * Initiate upgrade/subscription flow
   *
   * @param priceId Stripe Price ID
   * @param trialPeriodDays Optional trial duration
   */
  const createCheckout = async (priceId: string, trialPeriodDays?: number) => {
    setLoading(true)
    try {
      const response = await billingService.createCheckoutSession(priceId, trialPeriodDays)

      if (!response.success || !response.data) {
        // Check for specific error codes
        const alreadySubscribed = response.errors?.find(e => e.code === 'SUBSCRIPTION_ALREADY_EXISTS')
        if (alreadySubscribed) {
          message.warning('You already have this plan. Use "Manage Billing" to change plans.')
          return
        }

        message.error(response.message || 'Failed to create checkout session')
        return
      }

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl
    } catch (error) {
      console.error('Checkout error:', error)
      message.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Open Stripe Customer Portal for billing management
   */
  const openPortal = async () => {
    setLoading(true)
    try {
      const response = await billingService.createPortalSession()

      if (!response.success || !response.data) {
        // Check for specific error codes
        const noSubscription = response.errors?.find(e => e.code === 'NO_SUBSCRIPTION')
        if (noSubscription) {
          message.info('You don\'t have a subscription yet. Please upgrade to a paid plan first.')
          return
        }

        message.error(response.message || 'Failed to open billing portal')
        return
      }

      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portalUrl
    } catch (error) {
      console.error('Portal error:', error)
      message.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return {
    createCheckout,
    openPortal,
    loading
  }
}
